import Utils      from "@/core/utils/Utils";
import Validation from "@/core/services/Validation";
import debounce     from "lodash.debounce";
import Constants from "@/core/misc/Constants.js";
import type { ComponentPublicInstance } from "vue";

type ControlsHost = ComponentPublicInstance & {
  registerControl(control: ComponentPublicInstance): void;
  unregisterControl(control: ComponentPublicInstance): void;
};

let uidCounter = 1;

export default defineComponent({
  name: 'VBaseControl',

  props: {
    // ==================== Инфраструктура ====================

    /** Плейсхолдер контрола */
    placeholder:     { type: String,  default: null },
    /** Контрол включен/выключен. В выключенном состоянии изменение данных в компоненте блокируется. */
    disabled:        { type: Boolean, default: false },
    /** Если контрол заблокирован, то в ряде случаев нужно до пользователя как-то донести почему же. */
    disabledReasons: { type: Array,   default: null },

    // ==================== Лейбл ====================

    /** Лейбл контрола */
    label:       { type: [String, Number], default: null },
    /** Ширина лейбла. */
    labelWidth:  { type: String, default: null, validator: (value: string) => Constants.CSS_SIZE_REGEXP.test(value) !== false },
    /** Вывод контрола — вертикальный или горизонтальный. Влияет на вывод лейбла — сверху или слева. Возможные значения: 'horizontal', 'vertical'. */
    layout:      { type: String, default: 'vertical', validator: (value: string) => Constants.CONTROL_LAYOUTS.includes(value) },
    /** Если передан — прячет астериск обязательного поля. */
    hideAsterisk: { type: Boolean, default: false },

    // ==================== Валидация: булевы флаги ====================

    /** Значение не должно быть пустым. */
    required: { type: Boolean, default: false },
    /** Значение должно быть корректным email. */
    email:    { type: Boolean, default: false },
    /** Значение должно быть корректным именем (буквы/пробелы/дефисы). */
    name:     { type: Boolean, default: false },
    /** Значение должно быть корректным телефоном. */
    phone:    { type: Boolean, default: false },

    // ==================== Валидация: числовые ограничения ====================

    /** Минимальное значение (для числовых контролов). */
    min:       { type: Number, default: null },
    /** Максимальное значение (для числовых контролов). */
    max:       { type: Number, default: null },
    /** Минимальная длина строки. */
    minLength: { type: Number, default: null },
    /** Максимальная длина строки. */
    maxLength: { type: Number, default: null },

    // ==================== Валидация: кастомные функции ====================

    /** Кастомный валидатор. Возвращает строку (текст ошибки), null (ок) или Promise со строкой/null. Можно передать массив функций. */
    validateMethod: { type: [Function, Array], default: null },
    /** Предупреждение. В отличие от validateMethod, результат НЕ влияет на isValid и не блокирует submit формы. */
    warningMethod:  { type: [Function, Array], default: null },
    /** Сообщение об успехе. */
    successMethod:  { type: Function,          default: null },
  },

  data() {
    // self нужен потому что debounce оборачивает функцию и при вызове
    // теряется this Vue-инстанса — берём ссылку заранее.
    const self = this;

    return {
      isControl:  true,
      isErrorsVisible:     true,

      uid:        this.generateUid(),
      validation: Validation.fromProps(this as any),

      /**
       * Отложенный вызов validate() — срабатывает через 1 секунду
       * после последнего обращения. Используется когда внешний код
       * массово меняет значение (например, через v-model несколько
       * раз подряд) и валидировать на каждое изменение нет смысла.
       */
      debounceValidation: debounce(function () {
        if (typeof self.validate === 'function') {
          self.validate();
        }
      }, 1000),
    };
  },

  computed: {
    /**
     * Базовый набор CSS-классов корневого элемента контрола.
     * Наследники переопределяют controlClasses и добавляют свои,
     * читая базовый набор через this.getBaseControlClasses().
     */
    controlClasses(): string[] {
      return this.getBaseControlClasses();
    },

    labelStyles(): Record<string, string> | null {
      if (this.labelWidth === null) return null;
      return { flex: `0 0 ${this.labelWidth}` };
    },

    showAsterisk(): boolean {
      return this.required === true && this.hideAsterisk === false;
    },

    isDisabledHintVisible(): boolean {
      return this.disabled === true && Utils.isNotEmptyArray(this.disabledReasons);
    },

    /** Причины одной строкой — тултип принимает текст, а не список. */
    disabledHintText(): string {
      if (Utils.isNotEmptyArray(this.disabledReasons) === true) {
        return this.disabledReasons.join('\n');
      }
      else {
        return '';
      }
    },
  },

  watch: {
    /**
     * Правила Validation собираются фабрикой один раз, а выключенность
     * контрола меняется по ходу дела — держим её в синхроне отдельно.
     */
    disabled: {
      immediate: true,
      handler(isDisabled: boolean): void {
        this.validation.setDisabled(isDisabled);
      },
    },
  },

  created() {
    this.registerInParentForm();
    this.registerInParentFormItem();
  },

  beforeUnmount() {
    this.unregisterInParentForm();
    this.unregisterInParentFormItem();
  },

  methods: {
    /**
     * Возвращает базовые CSS-классы корня контрола.
     * Используется и в собственном computed controlClasses, и наследниками
     * как кирпич для построения расширенного списка классов.
     */
    getBaseControlClasses(): string[] {
      const classes: string[] = [];
      classes.push(`layout-${this.layout}`);
      if (this.disabled === true)              classes.push('disabled');
      if (this.validation.isValid === false)   classes.push('with-error');
      if (this.validation.warnings.length > 0) classes.push('with-warning');
      return classes;
    },

    /**
     * Показывает причину выключенности по наведению на контрол.
     * Вешается на корневой элемент, а не на input: выключенный input
     * событий мыши не порождает, наведение на него до нас не дойдёт.
     */
    showDisabledHint(event: MouseEvent): void {
      if (this.isDisabledHintVisible === false) {
        return;
      }

      this.$core.tooltip.show(event.currentTarget, this.disabledHintText);
    },

    hideDisabledHint(): void {
      this.$core.tooltip.hide();
    },

    /**
     * Генерирует уникальный id для контрола вида 'vinputnumberrus_42'.
     * Используется для связки label[for] ↔ input[id] и как ключ в VForm.controls.
     */
    generateUid(): string {
      const prefix = this.$options?.name === null || this.$options?.name === undefined
        ? 'unnamed'
        : this.$options.name.toLowerCase();
      return `${prefix}_${uidCounter++}`;
    },

    registerInParentForm(): void {
      const form = this._findParentByName('VForm');
      if (form !== null) {
        form.registerControl(this);
      }
    },

    unregisterInParentForm(): void {
      const form = this._findParentByName('VForm');
      if (form !== null) {
        form.unregisterControl(this);
      }
    },

    async validateForce(): Promise<void> {
      this.validation.markTouched();
      this.validation.markDirty();
      if (typeof this.validate === 'function') {
        await this.validate();
      }
    },

    validationReset(): void {
      this.validation.reset();
    },

    /**
     * Спрятать собственный блок сообщений (errors/warnings/success/helper-text).
     * Используется родителем-агрегатором (например, VFormItem), который
     * показывает сообщения от всех детей в одном месте.
     */
    hideErrors(): void {
      this.isErrorsVisible = false;
    },

    /**
     * Снова показать собственный блок сообщений.
     * Вызывается родителем при unregister'е дочернего контрола.
     */
    showErrors(): void {
      this.isErrorsVisible = true;
    },

    registerInParentFormItem(): void {
      const formItem = this._findParentByName('VFormItem');
      if (formItem !== null) {
        formItem.registerControl(this);
      }
    },

    unregisterInParentFormItem(): void {
      const formItem = this._findParentByName('VFormItem');
      if (formItem !== null) {
        formItem.unregisterControl(this);
      }
    },

    _findParentByName(targetName: string): ControlsHost | null {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options?.name === targetName) {
          return parent as ControlsHost;
        }
        else {
          parent = parent.$parent;
        }
      }
      return null;
    },

  },
});
